import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Search, 
  Plus, 
  Calendar,
  Mail,
  Phone,
  FileText,
  Clock,
  Trash2,
  MenuSquare,
  LayoutGrid,
  Edit
} from 'lucide-react';
import { fetchClients, deleteClient, updateClientStatus, fetchClientSessions } from '../../lib/supabase';
import NewClientModal from './NewClientModal';
import EditClientModal from './EditClientModal';
import ListView from './ListView';
import EditSessionModal from './EditSessionModal';
import ClientSelectionModal from './ClientSelectionModal';

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const formatGradeYear = (gradeYear, clientType) => {
  if (!gradeYear) return '';
  
  // For school students, format as "Xth Grade" if it's just a number
  if (
    (clientType === 'below_8th' || clientType === '8th_10th' || clientType === 'puc') && 
    /^[0-9]+$/.test(gradeYear)
  ) {
    // Handle special cases for ordinal suffixes
    let suffix = "th";
    if (gradeYear.endsWith('1') && !gradeYear.endsWith('11')) suffix = "st";
    if (gradeYear.endsWith('2') && !gradeYear.endsWith('12')) suffix = "nd";
    if (gradeYear.endsWith('3') && !gradeYear.endsWith('13')) suffix = "rd";
    
    return `${gradeYear}${suffix} Grade`;
  }
  
  return gradeYear;
};

const getClientTypeLabel = (type) => {
  const typeMap = {
    'below_8th': 'Below 8th Grade',
    '8th_10th': '8th to 10th Grade',
    'puc': 'PUC (11th & 12th)',
    'college': 'College Student',
    'professional': 'Professional'
  };
  return typeMap[type] || capitalizeFirstLetter(type);
};

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'cancelled', label: 'Cancelled' }
];

const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'completed':
      return 'bg-blue-100 text-blue-800';
    case 'on-hold':
      return 'bg-yellow-100 text-yellow-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getBorderColor = (status) => {
  switch (status) {
    case 'active':
      return 'border-green-300';
    case 'completed':
      return 'border-blue-300';
    case 'on-hold':
      return 'border-yellow-300';
    case 'cancelled':
      return 'border-red-300';
    default:
      return 'border-gray-300';
  }
};

const ClientCard = ({ client, onViewDetails, onDelete, onEdit, navigate }) => {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [showLastUpdated, setShowLastUpdated] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [nextSession, setNextSession] = useState(null);
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);

  useEffect(() => {
    const fetchNextSession = async () => {
      try {
        const sessions = await fetchClientSessions(client.id);
        
        // Find the next upcoming session - use 'scheduled' instead of 'upcoming'
        const now = new Date();
        const upcomingSessions = sessions
          .filter(s => s.status === 'scheduled' && new Date(s.session_date) > now)
          .sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
        
        if (upcomingSessions.length > 0) {
          setNextSession(upcomingSessions[0]);
        }
      } catch (err) {
        console.error(`Error fetching sessions for client ${client.id}:`, err);
      }
    };
    
    fetchNextSession();
  }, [client.id]);

  const handleStatusChange = async (e) => {
    e.stopPropagation(); // Prevent card click
    const newStatus = e.target.value;
    
    // Reset error
    setStatusError('');
    
    // Check if trying to set to 'completed'
    if (newStatus === 'completed') {
      // Check if there's a scheduled session that hasn't happened yet
      if (client.next_session_date) {
        const sessionDate = new Date(client.next_session_date);
        const now = new Date();
        
        if (sessionDate > now) {
          setStatusError('Cannot mark as completed: scheduled session has not occurred yet');
          // Reset the select value back to the previous status
          e.target.value = client.status;
          
          // Show error message temporarily
          setTimeout(() => {
            setStatusError('');
          }, 5000);
          
          return;
        }
      }
    }
    
    // Proceed with status update
    try {
      await updateClientStatus(client.id, newStatus);
      // Optionally refresh the client list or update local state
      window.location.reload();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleDeleteClient = async () => {
    try {
      console.log("Deleting client with ID:", client.id);
      await deleteClient(client.id);
      console.log("Deleted client successfully!");

      // Call the onDelete prop passed from the parent component
      onDelete(client.id);

      setConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting client:', err);
      alert('Failed to delete client');
    }
  };

  const toggleLastUpdated = (e) => {
    e.stopPropagation();
    setShowLastUpdated(!showLastUpdated);
  };

  const handleSessionClick = (e) => {
    e.stopPropagation();
    
    if (nextSession) {
      // If there's a session, open the edit modal
      setIsEditSessionModalOpen(true);
    } else {
      // If no session, navigate to client profile
      navigate(`/admin/dashboard/clients/${client.id}`);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onEdit(client);
  };

  return (
    <div 
      onClick={() => onViewDetails(client.id)}
      className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 relative cursor-pointer ${getBorderColor(client.status)}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{client.name}</h3>
          <p className="text-sm text-gray-500">{getClientTypeLabel(client.type)}</p>
          <p className="text-xs font-medium text-teal-600 mt-1">
            Reg No: {client.registration_number}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <select
            value={client.status}
            onChange={handleStatusChange}
            onClick={(e) => e.stopPropagation()}
            className={`px-3 py-1 rounded-full text-sm border-2 transition-colors cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 ${getStatusColor(client.status)}`}
          >
            {statusOptions.map(option => (
              <option 
                key={option.value} 
                value={option.value}
                className="bg-white text-gray-700"
              >
                {option.label}
              </option>
            ))}
          </select>
          {statusError && (
            <span className="text-xs text-red-500 mt-1">
              {statusError}
            </span>
          )}
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center text-gray-600">
          <Mail className="h-4 w-4 mr-2" />
          <span className="text-sm">{client.email}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Phone className="h-4 w-4 mr-2" />
          <span className="text-sm">{client.phone}</span>
        </div>
        {client.grade_year && (
          <div className="flex items-center text-gray-600">
            <FileText className="h-4 w-4 mr-2" />
            <span className="text-sm">
              {formatGradeYear(client.grade_year, client.type)}
            </span>
          </div>
        )}
        <div 
          className="flex items-center text-gray-600 cursor-pointer hover:text-teal-600"
          onClick={handleSessionClick}
        >
          <Calendar className="h-4 w-4 mr-2" />
          <span className="text-sm">
            {nextSession ? (
              <>
                {new Date(nextSession.session_date).toLocaleDateString()} - {nextSession.session_type}
              </>
            ) : (
              'No session scheduled'
            )}
          </span>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div className="relative">
            <button
              onClick={toggleLastUpdated}
              className="flex items-center p-1 hover:bg-gray-100 rounded-md"
              title="Last Updated"
            >
              <Clock className="h-4 w-4 mr-1 text-gray-400" />
              <span className="text-sm text-gray-500">Info</span>
            </button>
            
            {showLastUpdated && (
              <div 
                onClick={(e) => e.stopPropagation()} 
                className="absolute top-0 left-0 mt-8 bg-white rounded-md shadow-lg z-10 p-2 text-sm text-gray-600 w-48 border"
              >
                <p>Last updated:</p>
                <p className="font-medium">
                  {new Date(client.created_at).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={handleEditClick}
              className="p-1 text-teal-500 hover:text-teal-700 hover:bg-teal-50 rounded-md transition-colors"
              title="Edit Client"
            >
              <Edit className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(true);
              }}
              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              title="Delete Client"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Overlay */}
          <div className="fixed inset-0 bg-black opacity-50"></div>

          {/* Modal Content */}
          <div className="bg-white p-6 rounded shadow-lg relative z-10 max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this client?</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmOpen(false);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClient();
                }}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {isEditSessionModalOpen && nextSession && (
  <EditSessionModal
    isOpen={isEditSessionModalOpen}
    onClose={() => setIsEditSessionModalOpen(false)}
    session={nextSession}
    onUpdate={() => {
      // Refresh the session data
      const fetchNextSession = async () => {
        try {
          const sessions = await fetchClientSessions(client.id);
          
          // Find the next upcoming session
          const now = new Date();
          const upcomingSessions = sessions
            .filter(s => s.status === 'scheduled' && new Date(s.session_date) > now)
            .sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
          
          if (upcomingSessions.length > 0) {
            setNextSession(upcomingSessions[0]);
          } else {
            setNextSession(null);
          }
        } catch (err) {
          console.error(`Error fetching sessions for client ${client.id}:`, err);
        }
      };
      
      fetchNextSession();
    }}
  />
)}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCancelled, setShowCancelled] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    // Check if on mobile and set grid view by default
    if (window.innerWidth < 768) {
      return 'grid';
    }
    return 'list';
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('grid');
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await fetchClients();
      setClients(data);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (clientId) => {
    navigate(`/admin/dashboard/clients/${clientId}`);
  };

  const handleDeleteClient = (clientId) => {
    setClients(prevClients => prevClients.filter(client => client.id !== clientId));
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.phone?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    const matchesType = typeFilter === 'all' || client.type === typeFilter;
    
    // Hide cancelled clients unless toggle is on
    if (!showCancelled && client.status === 'cancelled') {
      return false;
    }
    
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Client Management</h1>
          <p className="text-gray-600">Manage and track your clients' progress</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setIsNewModalOpen(true)}
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            New Client
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Edit size={20} className="mr-2" />
            Edit Client
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4 border border-teal-500">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search clients..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="on-hold">On Hold</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Types</option>
              <option value="below_8th">Below 8th Grade</option>
              <option value="8th_10th">8th to 10th Grade</option>
              <option value="puc">PUC (11th & 12th)</option>
              <option value="college">College</option>
              <option value="professional">Professional</option>
            </select>
            <div className="flex items-center gap-2 hidden md:flex">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">Show Cancelled Clients</span>
            </label>
          </div>
          </div>
        </div>
      </div>
        <div className="hidden md:flex justify-end mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-md ${
                viewMode === 'list' 
                  ? 'bg-teal-600 text-white' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <MenuSquare className="h-5 w-5" />
                <span>List</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 rounded-md ${
                viewMode === 'grid' 
                  ? 'bg-teal-600 text-white' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <LayoutGrid className="h-5 w-5" />
                <span>Grid</span>
              </div>
            </button>
          </div>
        </div>

      {/* Client Grid */}
      {filteredClients.length > 0 ? (
        <>
          {/* Always use list view on desktop when selected */}
          {viewMode === 'list' && (
            <div className="hidden md:block">
              <ListView 
                clients={filteredClients}
                onViewDetails={handleViewDetails}
                onDelete={handleDeleteClient}
                onEdit={handleEditClient}
                navigate={navigate}
              />
            </div>
          )}
          
          {/* Show grid view based on selection on desktop, always on mobile */}
          {(viewMode === 'grid' || window.innerWidth < 768) && (
            <div className={`grid grid-cols-1 ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
              {filteredClients.map((client) => (
                <ClientCard
                  key={client.id}
                  client={client}
                  onViewDetails={handleViewDetails}
                  onDelete={handleDeleteClient}
                  onEdit={handleEditClient}
                  navigate={navigate}
                />
              ))}
            </div>
          )}
        </>
      ) : (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No clients found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding a new client'}
            </p>
          </div>
        )}
      {/* New Client Modal */}
      <NewClientModal 
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        onClientAdded={(newClient) => {
          loadClients(); // Refresh the client list
          setIsNewModalOpen(false);
        }}
      />
      
      {/* Edit Client Modal */}
      {isEditModalOpen && (
        selectedClient ? (
          <EditClientModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedClient(null);
            }}
            client={selectedClient}
            onClientUpdated={() => {
              loadClients(); // Refresh the client list
              setIsEditModalOpen(false);
              setSelectedClient(null);
            }}
          />
        ) : (
          <ClientSelectionModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            clients={clients}
            onSelectClient={(client) => {
              setSelectedClient(client);
            }}
          />
        )
      )}
    </div>
  );
};

export default AdminDashboard;